import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import type { Campaign } from '../../types/campaign';
import type { AccountCampaign } from '../../types/accountCampaign';
import {
  REFRESHABLE_FIELD_BY_KEY,
  refreshableFieldsBySection,
  type RefreshableFieldEntry,
  type RefreshableFieldKey,
} from '../../lib/refreshableFields';
import { TruncatedFieldPreview } from './TruncatedFieldPreview';
import { SectionCheckbox } from './SectionCheckbox';
import { ConflictWarning, type ConflictLine } from './ConflictWarning';

// Granular Refresh spec §2. The BULK granular refresh modal — distinct
// from the single-row RefreshFromCampaignModal (a yes/no confirm used by
// the row editor; per-row field refresh is explicitly future, spec §10).
//
// Section-grouped field selection with expandable previews, section
// select-all, a live conflict warning (how many selected rows each
// selected field would overwrite), a running total, and an atomic submit
// that keeps the modal open with a retryable error on failure.

type Props = {
  campaign: Campaign;
  // The actual selected rows (not just ids) — needed for the per-field
  // conflict count (spec §2.6). The submit path derives ids from these.
  selectedRows: AccountCampaign[];
  onClose: () => void;
  // Granular Refresh spec §4/§8. Resolves on a successful atomic refresh
  // (the parent owns the API call + cache invalidation); rejects with an
  // Error whose message is shown inline so the AM can retry without losing
  // their field selection (spec §8 edge 31).
  onSubmit: (fieldKeys: RefreshableFieldKey[]) => Promise<void>;
};

function fieldLabel(entry: RefreshableFieldEntry, campaign: Campaign): string {
  if (!entry.count) return entry.label;
  return `${entry.label} (${entry.count(campaign)})`;
}

export function BulkRefreshFromCampaignModal({
  campaign,
  selectedRows,
  onClose,
  onSubmit,
}: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [selected, setSelected] = useState<Set<RefreshableFieldKey>>(
    () => new Set(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const toggle = (key: RefreshableFieldKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const groups = refreshableFieldsBySection();

  // Section tri-state + select-all (spec §2.4).
  const sectionState = (
    keys: RefreshableFieldKey[],
  ): 'all' | 'some' | 'none' => {
    const n = keys.filter((k) => selected.has(k)).length;
    if (n === 0) return 'none';
    return n === keys.length ? 'all' : 'some';
  };

  const toggleSection = (keys: RefreshableFieldKey[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allOn = keys.every((k) => next.has(k));
      for (const k of keys) {
        if (allOn) next.delete(k);
        else next.add(k);
      }
      return next;
    });
  };

  const totalSelected = selected.size;

  const handleSubmit = async () => {
    if (totalSelected === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit([...selected]);
      onClose();
    } catch (err) {
      // Atomic failure → no partial state server-side; keep the modal +
      // field selection so the AM can retry (spec §8 edge 31).
      setError(err instanceof Error ? err.message : 'Refresh failed.');
      setBusy(false);
    }
  };

  // Conflict warning (spec §2.6): for each SELECTED field, how many of the
  // selected rows carry a value that differs from the Campaign (and would
  // therefore be overwritten). Recomputed as the selection / rows change.
  const conflicts = useMemo<ConflictLine[]>(() => {
    const lines: ConflictLine[] = [];
    for (const entry of REFRESHABLE_FIELD_BY_KEY.values()) {
      if (!selected.has(entry.key)) continue;
      const n = selectedRows.filter((r) =>
        entry.isModified(r, campaign),
      ).length;
      if (n > 0) lines.push({ label: entry.label, rows: n });
    }
    return lines;
  }, [selected, selectedRows, campaign]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div
        className="flex max-h-[85vh] w-full max-w-[700px] flex-col rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-refresh-title"
      >
        {/* Header */}
        <div className="border-b border-slate-200 p-6 pb-4">
          <h2
            id="bulk-refresh-title"
            className="text-lg font-semibold text-slate-900"
          >
            Refresh from Campaign
          </h2>
          <dl className="mt-2 space-y-0.5 text-sm text-slate-700">
            <div className="flex gap-2">
              <dt className="text-slate-500">Source Campaign:</dt>
              <dd className="font-medium text-slate-900">{campaign.name}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-slate-500">Selected rows:</dt>
              <dd className="font-medium text-slate-900">
                {selectedRows.length}
              </dd>
            </div>
          </dl>
        </div>

        {/* Sections (conflict warning pinned above them, spec §2.6) */}
        <div className="min-h-0 flex-1 overflow-y-auto p-6 pt-4 space-y-5">
          <ConflictWarning lines={conflicts} />
          {groups.map((group) => (
            <section
              key={group.section}
              className="rounded-md border border-slate-200 bg-slate-50/60"
            >
              <SectionCheckbox
                label={group.label}
                count={group.fields.length}
                state={sectionState(group.fields.map((f) => f.key))}
                onToggle={() =>
                  toggleSection(group.fields.map((f) => f.key))
                }
              />
              <ul className="divide-y divide-slate-100">
                {group.fields.map((entry) => {
                  const checked = selected.has(entry.key);
                  return (
                    <li key={entry.key} className="flex items-start gap-3 px-4 py-3">
                      <label className="flex cursor-pointer items-center gap-3">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300"
                          checked={checked}
                          onChange={() => toggle(entry.key)}
                        />
                        <span className="text-sm font-medium text-slate-800">
                          {fieldLabel(entry, campaign)}
                        </span>
                      </label>
                      {/* Preview is a sibling, NOT inside the label, so
                          expanding it never toggles the checkbox. */}
                      <div className="min-w-0 flex-1">
                        <TruncatedFieldPreview
                          text={entry.preview(campaign)}
                          items={entry.items?.(campaign)}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-6 pt-4">
          {error && (
            <p
              role="alert"
              className="mb-3 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800"
            >
              {error} Nothing was changed — adjust your selection and retry.
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            {/* Total-selected indicator (spec §2.5); disabled at N=0 or
                while the atomic refresh is in flight. */}
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={totalSelected === 0 || busy}
            >
              {busy
                ? 'Refreshing…'
                : totalSelected === 0
                  ? 'Refresh from Campaign'
                  : `Refresh ${totalSelected} selected field${
                      totalSelected === 1 ? '' : 's'
                    } →`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
